import View from '../atoms/view';
import { P } from '../atoms/text';
import { PulsingDot } from '../atoms/pulsingDot';
import { Colors, Spacing, Styles } from '../../constants/theme';

export type LocationDisplay = {
  name: string;
  address: string;
};

type DestinationCardProps = {
  title?: string;
  fromLocation?: LocationDisplay;
  toLocation?: LocationDisplay | null;
  onPressFrom?: () => void;
  onPressTo?: () => void;
  isTransferActive?: boolean;
  showLocation?: boolean;
  showViewAction?: boolean;
};

const DOT_COUNT = 5;

const RouteTimeline = () => (
  <View extend={styles.timeline}>
    <View extend={styles.dot} />
    <View extend={styles.dottedLine}>
      {Array.from({ length: DOT_COUNT }).map((_, index) => (
        <View key={index} extend={styles.dotSegment} />
      ))}
    </View>
    <View extend={styles.dot} />
  </View>
);

export const DestinationCard = ({
  title,
  fromLocation,
  toLocation,
  onPressFrom,
  onPressTo,
  isTransferActive = false,
  showLocation = true,
  showViewAction = false,
}: DestinationCardProps) => {
  return (
    <View extend={styles.infoBox}>
      {!isTransferActive && (
        <P strong noPadding>
          Destination
        </P>
      )}
      <View extend={[styles.card, isTransferActive && styles.cardActive]}>
        {isTransferActive ? (
          <View extend={[styles.statusRow, showViewAction && styles.statusRowWithAction]}>
            <View extend={styles.statusRowContent}>
              <PulsingDot />
              <View
                as="span"
                extend={{
                  color: Colors.light.green,
                  fontWeight: 800,
                  lineHeight: 1,
                  margin: 0,
                  padding: 0,
                }}
              >
                Transfer in Progress
              </View>
            </View>
            {showViewAction && (
              <View extend={styles.viewAction}>
                <P noPadding extend={styles.viewActionText}>
                  View
                </P>
              </View>
            )}
          </View>
        ) : (
          title && <P noPadding>{title}</P>
        )}
        {showLocation && (
          <View extend={styles.routeRow}>
            <RouteTimeline />
            <View extend={styles.content}>
              <View
                as="button"
                onClick={onPressFrom}
                disabled={isTransferActive || !onPressFrom}
                extend={styles.stop}
              >
                <P noPadding extend={styles.label}>
                  FROM
                </P>
                <P noPadding>{fromLocation?.name}</P>
                {fromLocation?.address ? (
                  <P noPadding extend={styles.address}>
                    {fromLocation.address}
                  </P>
                ) : null}
              </View>
              <View
                as="button"
                onClick={onPressTo}
                disabled={isTransferActive || !onPressTo}
                extend={styles.stop}
              >
                <P noPadding extend={styles.label}>
                  TO
                </P>
                {toLocation ? (
                  <>
                    <P noPadding>{toLocation.name}</P>
                    {toLocation.address ? (
                      <P noPadding extend={styles.address}>
                        {toLocation.address}
                      </P>
                    ) : null}
                  </>
                ) : (
                  <P noPadding extend={styles.placeholder}>
                    Select destination
                  </P>
                )}
              </View>
            </View>
          </View>
        )}
      </View>
    </View>
  );
};

const styles = {
  infoBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: `${4 * Spacing.gap}px`,
    width: '100%',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    backgroundColor: Colors.light.secondaryBackground,
    borderRadius: `${Styles.BorderRadius}px`,
    ...Colors.shadowStyle,
    padding: `${10 * Spacing.padding}px`,
    gap: `${10 * Spacing.gap}px`,
    boxSizing: 'border-box',
  },
  cardActive: {
    backgroundColor: Colors.light.lightGreen,
    border: `1px solid ${Colors.light.green}`,
  },
  statusRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: `${6 * Spacing.gap}px`,
  },
  statusRowWithAction: {
    justifyContent: 'space-between',
  },
  statusRowContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: `${6 * Spacing.gap}px`,
    lineHeight: 1,
  },
  viewAction: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: `${4 * Spacing.gap}px`,
  },
  viewActionText: {
    color: Colors.light.green,
    fontWeight: 500,
    margin: 0,
  },
  routeRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: `${10 * Spacing.gap}px`,
  },
  timeline: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: `${4 * Spacing.padding}px`,
    paddingBottom: `${4 * Spacing.padding}px`,
  },
  dot: {
    width: '10px',
    height: '10px',
    borderRadius: '5px',
    backgroundColor: Colors.light.lightGrey,
    border: `1px solid ${Colors.light.borderColor}`,
    flexShrink: 0,
  },
  dottedLine: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: `${4 * Spacing.padding}px`,
    paddingBottom: `${4 * Spacing.padding}px`,
    gap: '3px',
  },
  dotSegment: {
    width: '2px',
    height: '4px',
    borderRadius: '1px',
    backgroundColor: Colors.light.borderColor,
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: `${8 * Spacing.gap}px`,
    minWidth: 0,
  },
  stop: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: `${2 * Spacing.gap}px`,
    background: 'transparent',
    border: 'none',
    padding: 0,
    margin: 0,
    textAlign: 'left',
    cursor: 'default',
    font: 'inherit',
    color: 'inherit',
    width: '100%',
  },
  label: {
    fontSize: '12px',
    fontWeight: 600,
    color: Colors.light.placeholder,
    letterSpacing: '0.5px',
    margin: 0,
  },
  address: {
    fontSize: '14px',
    color: Colors.light.placeholder,
    margin: 0,
  },
  placeholder: {
    color: Colors.light.placeholder,
    margin: 0,
  },
};
